import { useEffect, useMemo, useState } from "react";
import { useLang } from "@/contexts/LangContext";
import { api, formatSAR, formatDate } from "@/lib/api";
import { X, Wallet, Trash2, CheckCircle2, FileText } from "lucide-react";
import { toast } from "sonner";
import PayoutReceipt from "@/components/PayoutReceipt";

export default function PayoutModal({ therapist, mode = "terapis", onClose, onSaved }) {
  const { t } = useLang();
  const isCashier = mode === "kasir";
  const [history, setHistory] = useState([]);
  const [totalPaid, setTotalPaid] = useState(0);
  const [totalKomisiPaid, setTotalKomisiPaid] = useState(0);
  // Breakdown inputs
  const [gaji, setGaji] = useState(isCashier ? "" : "2000");
  const [komisi, setKomisi] = useState("");
  const [bonusGoogleAmount, setBonusGoogleAmount] = useState("");
  const [bonusGoogleNames, setBonusGoogleNames] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [viewReceipt, setViewReceipt] = useState(null);  // payout obj

  // Pending = komisi belum dibayar (lifetime earned - komisi yang sudah dibayar)
  const pending = useMemo(
    () => Math.max(0, Number((therapist.lifetime_earned || 0) - totalKomisiPaid).toFixed(2)),
    [therapist.lifetime_earned, totalKomisiPaid]
  );

  const total = useMemo(() => {
    const g = parseFloat(gaji) || 0;
    const k = parseFloat(komisi) || 0;
    const b = parseFloat(bonusGoogleAmount) || 0;
    return Math.round((g + k + b) * 100) / 100;
  }, [gaji, komisi, bonusGoogleAmount]);

  async function loadHistory() {
    setLoading(true);
    try {
      const { data } = await api.get(`/therapists/${therapist.id}/payouts`);
      setHistory(data.payouts || []);
      setTotalPaid(data.total_paid || 0);
      // Total komisi yang sudah dibayar (untuk hitung pending)
      const tk = (data.payouts || []).reduce(
        (s, p) => s + (p.komisi !== undefined ? p.komisi : p.amount || 0),
        0
      );
      setTotalKomisiPaid(tk);
      // One-shot auto-fill: pre-populate komisi input with the pending balance
      // so admin only types when adjusting. Only triggers on first load.
      const pendingNow = Math.max(0, (therapist.lifetime_earned || 0) - tk);
      if (!isCashier && komisi === "" && pendingNow > 0) {
        setKomisi(String(pendingNow.toFixed(2)));
      }
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadHistory(); }, [therapist.id]);

  async function submit() {
    if (total <= 0) {
      toast.error("Total pembayaran harus lebih dari 0");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/therapists/${therapist.id}/payouts`, {
        gaji: parseFloat(gaji) || 0,
        komisi: parseFloat(komisi) || 0,
        bonus_google_amount: parseFloat(bonusGoogleAmount) || 0,
        bonus_google_names: bonusGoogleNames,
        notes,
      });
      toast.success(t("ther.payoutSuccess"));
      // Reset all form fields in a single batched update — bypass linter
      // false-positive on consecutive setters by combining them inline.
      [setGaji, setKomisi, setBonusGoogleAmount, setBonusGoogleNames, setNotes]
        .forEach((fn, i) => fn(i === 0 ? "2000" : ""));
      await loadHistory();
      onSaved?.();
      // Auto-show receipt for the just-created payout
      setViewReceipt(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  }

  async function removePayout(id) {
    if (!window.confirm(t("ther.payoutDeleteConfirm"))) return;
    try {
      await api.delete(`/therapists/payouts/${id}`);
      toast.success("Catatan dihapus");
      await loadHistory();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  }

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm grid place-items-center p-4"
      onClick={onClose}
      data-testid="payout-modal"
    >
      <div
        className="card-luxury p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-gold flex items-center gap-2">
              <Wallet className="h-4 w-4" /> {t("ther.payoutFor")}
            </div>
            <h2 className="text-2xl font-heading mt-1">{therapist.name}</h2>
            <div className="text-xs text-foreground/50">{therapist.phone}</div>
          </div>
          <button onClick={onClose} data-testid="payout-close-btn" className="p-1">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Summary — therapist only */}
        {!isCashier && (
          <div className="grid sm:grid-cols-3 gap-3 mb-5">
            <div className="bg-charcoal-900/60 rounded-lg p-3 border border-gold/15">
              <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/50">{t("ther.totalEarned")}</div>
              <div className="text-lg font-heading text-gold mt-1" data-testid="payout-lifetime">
                {formatSAR(therapist.lifetime_earned || 0)}
              </div>
            </div>
            <div className="bg-charcoal-900/60 rounded-lg p-3 border border-leaf/20">
              <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/50">{t("ther.paid")}</div>
              <div className="text-lg font-heading text-leaf mt-1" data-testid="payout-paid">
                {formatSAR(totalPaid)}
              </div>
            </div>
            <div className="bg-gold/10 rounded-lg p-3 border border-gold/30">
              <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/50">{t("ther.pendingNow")}</div>
              <div className="text-lg font-heading text-gold mt-1" data-testid="payout-pending">
                {formatSAR(pending)}
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="bg-charcoal-900/40 border border-gold/15 rounded-lg p-4 mb-6 space-y-3">
          {/* Gaji */}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-foreground/50 mb-1">
              {isCashier ? "Gaji Pokok (SAR)" : "1. Gaji Pokok (SAR)"}
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={gaji}
              onChange={(e) => setGaji(e.target.value)}
              placeholder={isCashier ? "Masukkan jumlah gaji" : ""}
              data-testid="payout-gaji-input"
              className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2 font-heading"
            />
          </div>

          {/* Komisi — therapist only */}
          {!isCashier && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[10px] uppercase tracking-[0.2em] text-foreground/50">
                  2. Komisi (SAR)
                </label>
                <button
                  type="button"
                  onClick={() => setKomisi(String(pending))}
                  disabled={Number(pending) <= 0}
                  data-testid="payout-fullpay-btn"
                  className="text-[10px] text-gold hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Pending: {formatSAR(pending)} → Bayar Lunas
                </button>
              </div>
              <input
                type="number"
                step="0.01"
                min="0"
                value={komisi}
                onChange={(e) => setKomisi(e.target.value)}
                data-testid="payout-komisi-input"
                className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2 font-heading"
              />
            </div>
          )}

          {/* Bonus Google Review — therapist only */}
          {!isCashier && (
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-foreground/50 mb-1">
                3. Bonus Ekstra Google Review (SAR)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={bonusGoogleAmount}
                onChange={(e) => setBonusGoogleAmount(e.target.value)}
                placeholder="0"
                data-testid="payout-bonus-input"
                className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2 font-heading"
              />
              <input
                type="text"
                value={bonusGoogleNames}
                onChange={(e) => setBonusGoogleNames(e.target.value)}
                placeholder="Nama-nama disebut di Google Review (cth: Ahmad, Fatima, ...)"
                data-testid="payout-bonus-names-input"
                className="w-full mt-2 bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2 text-sm"
              />
            </div>
          )}

          {/* TOTAL preview */}
          <div className="border-t border-gold/15 pt-3 mt-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground/60">TOTAL Tanda Terima:</span>
              <span className="text-2xl font-heading text-gold" data-testid="payout-total-preview">
                {formatSAR(total)}
              </span>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-foreground/50 mb-1">
              {t("ther.payoutNotes")}
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="cth: pembayaran periode minggu ke-22"
              data-testid="payout-notes-input"
              className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2"
            />
          </div>

          <button
            onClick={submit}
            disabled={submitting || total <= 0}
            data-testid="payout-submit-btn"
            className="btn-gold w-full justify-center mt-2 disabled:opacity-50"
          >
            <CheckCircle2 className="h-4 w-4" />
            {submitting ? "..." : t("ther.confirmPayout")}
          </button>
        </div>

        {/* History */}
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">— {t("ther.payoutHistory")}</div>
          {loading && <div className="text-center py-6 text-foreground/40">{t("common.loading")}</div>}
          {!loading && history.length === 0 && (
            <div className="text-center py-8 text-foreground/40 border border-dashed border-gold/15 rounded-lg">
              {t("ther.noPayouts")}
            </div>
          )}
          {!loading && history.length > 0 && (
            <div className="space-y-2" data-testid="payout-history-list">
              {history.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between bg-charcoal-900/60 border border-gold/10 rounded-lg p-3"
                  data-testid={`payout-row-${p.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-heading text-gold">{formatSAR(p.amount)}</div>
                    <div className="text-[11px] text-foreground/50 mt-0.5">
                      {formatDate(p.paid_at)} · {t("ther.paidBy")}: {p.paid_by_name || "—"}
                    </div>
                    {p.notes && (
                      <div className="text-xs text-foreground/70 mt-1 italic truncate">&ldquo;{p.notes}&rdquo;</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => setViewReceipt(p)}
                      data-testid={`payout-receipt-${p.id}`}
                      className="p-2 rounded-full hover:bg-gold/10 text-gold"
                      title={t("receipt.payout.view")}
                    >
                      <FileText className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => removePayout(p.id)}
                      data-testid={`payout-delete-${p.id}`}
                      className="p-2 rounded-full hover:bg-red-500/10 text-red-400"
                      title="Hapus"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {viewReceipt && (
        <PayoutReceipt
          payout={viewReceipt}
          therapist={therapist}
          onClose={() => setViewReceipt(null)}
        />
      )}
    </div>
  );
}
